"use client"

import type React from "react"
import { useEffect, useState } from "react"
import axios from "axios"
import { useAuthStore } from "../store/authStore"
import { useChatStore } from "../store/chatStore"
import { env } from "../config/env"
import { toast } from "sonner"

interface Contact {
  contactUsername: string
  contactName: string
  addedAt: string
}

interface ContactListProps {
  onSelectContact?: () => void
}

const ContactList: React.FC<ContactListProps> = ({ onSelectContact }) => {
  const API_URL = env.API_URL
  const { token } = useAuthStore()
  const { setActiveChat, activeChat } = useChatStore()
  const [localContacts, setLocalContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(false)

  const fetchContacts = async () => {
    if (!token) return

    setLoading(true)
    try {
      const response = await axios.get(`${API_URL}/users/contacts/list`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      const contactsWithDetails = await Promise.all(
        response.data.data.map(async (contact: { contactUsername: string; addedAt: string }) => {
          try {
            const userResponse = await axios.get(`${API_URL}/users/${contact.contactUsername}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            return {
              contactUsername: contact.contactUsername,
              contactName: userResponse.data.data.name || contact.contactUsername,
              addedAt: contact.addedAt,
            }
          } catch (error) {
            console.error(`Failed to fetch details for ${contact.contactUsername}:`, error)
            toast.error(`Error showing contacts information of ${contact.contactUsername}`, {
              description: `Error fetching information: ${error}`
            })
            return {
              contactUsername: contact.contactUsername,
              contactName: contact.contactUsername,
              addedAt: contact.addedAt,
            }
          }
        }),
      )

      setLocalContacts(contactsWithDetails)
    } catch (error) {
      console.error("Failed to fetch contacts:", error)
      toast.error("Failed getting contacts", {
        description: `Error fetching contacts: ${error}`
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchContacts()
  }, [token])

  useEffect(() => {
    const handleRefreshContacts = () => {
      fetchContacts()
    }

    window.addEventListener("refreshContacts", handleRefreshContacts)

    return () => {
      window.removeEventListener("refreshContacts", handleRefreshContacts)
    }
  }, [token])

  if (loading) {
    return <div className="text-slate-400 p-4">Loading contacts...</div>
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {localContacts.length === 0 ? (
        <div className="text-slate-400 p-4 text-center">No contacts yet</div>
      ) : (
        <div className="space-y-1 p-2">
          {localContacts.map((contact) => (
            <button
              key={contact.contactUsername}
              onClick={() => {
                setActiveChat(contact.contactUsername)
                onSelectContact?.()
              }}
              className={`w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 rounded transition cursor-pointer ${
                activeChat === contact.contactUsername ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-700"
              }`}
            >
              <div className="font-medium text-sm sm:text-base">{contact.contactName}</div>
              <div className="text-xs opacity-70">@{contact.contactUsername}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default ContactList
